import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  RichTextChangeSource,
  RichTextEditorHandle,
  RichTextPasteDelta,
} from './rich-text.type';

const DEFAULT_FORMATS = ['bold', 'italic', 'underline', 'link'];

/** Stable hook for consumer styling — the vendor's own root class is not part of this module's contract. */
const ROOT_CLASS = 'rich-text-editor-root';

type RichTextEditorProps = {
  /** Uncontrolled by default — remount via `key` to load different content. */
  defaultValue?: string;
  value?: string;
  formats?: string[];
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  theme?: 'snow' | 'bubble';
  /** Quill's clipboard `matchVisual`. Off preserves authored markup on paste. */
  matchVisual?: boolean;
  /** Discards inbound formatting on paste, keeping only the text. */
  stripPasteFormatting?: boolean;
  /** Registers the colour format, required before colour marks will render. */
  registerColorFormat?: boolean;
  /**
   * Which editor change sources propagate to `onChange`. Defaults to user
   * edits only. Pass `['user', 'api']` when formatting is also applied
   * programmatically, as a colour picker does.
   */
  changeSources?: RichTextChangeSource[];
  onChange?: (html: string, meta: { text: string }) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  RichTextEditorProps
>(
  (
    {
      defaultValue,
      value,
      formats = DEFAULT_FORMATS,
      placeholder,
      readOnly,
      className,
      theme,
      matchVisual = false,
      stripPasteFormatting = false,
      registerColorFormat = false,
      changeSources = ['user'],
      onChange,
      onFocus,
      onBlur,
    },
    ref,
  ) => {
    const quillRef = useRef<ReactQuill>(null);

    useEffect(() => {
      if (!registerColorFormat) return;
      ReactQuill.Quill.register(ReactQuill.Quill.import('formats/color'), true);
    }, [registerColorFormat]);

    useEffect(() => {
      if (!stripPasteFormatting) return;
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      editor.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
        const paste = delta as unknown as RichTextPasteDelta;
        if (paste.ops) {
          paste.ops = paste.ops.map((op) => ({ insert: op.insert }));
        }
        return delta;
      });
    }, [stripPasteFormatting]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => quillRef.current?.focus(),
        getVendorEditor: () => quillRef.current?.getEditor() ?? null,
        getFormatApi: () => {
          const editor = quillRef.current?.getEditor();
          if (!editor) return null;

          return {
            // Quill's own toolbar formats as a user edit; its default source is
            // 'api', which consumers filter out, silently discarding the format.
            format: (name, value) => editor.format(name, value, 'user'),
            // Selection is null while focus is still settling, and the
            // editor dereferences the range without checking it first
            getFormat: () => {
              const selection = editor.getSelection();

              return selection ? editor.getFormat(selection) : {};
            },
            // The emitter is untyped underneath; the overloads demand a literal
            on: (event, handler) => {
              editor.on(event as 'text-change', handler);
            },
            off: (event, handler) => {
              editor.off(event as 'text-change', handler);
            },
          };
        },
      }),
      [],
    );

    const modules = {
      // Every toolbar is driven through `useRichTextToolbar`, never by DOM id
      toolbar: false,
      clipboard: { matchVisual },
    };

    const isControlled = value !== undefined;

    return (
      <ReactQuill
        ref={quillRef}
        className={[ROOT_CLASS, className].filter(Boolean).join(' ')}
        placeholder={placeholder}
        readOnly={readOnly}
        theme={theme}
        modules={modules}
        formats={formats}
        {...(isControlled ? { value } : { defaultValue })}
        onChange={(html, _delta, source, editor) => {
          if (!changeSources.includes(source as RichTextChangeSource)) return;
          onChange?.(html, { text: editor.getText() });
        }}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  },
);

RichTextEditor.displayName = 'RichTextEditor';
